import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  ToggleLeft,
  ToggleRight,
  Globe,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import {
  fetchMarketingMessages,
  createMarketingMessage,
  updateMarketingMessage,
  deleteMarketingMessage,
  fetchCountriesForDropdown,
  MarketingMessage,
} from '../services/api.ts';
import './MarketingMessages.css';

interface Country {
  id: number;
  name: string;
  code: string;
}

const ICON_OPTIONS = [
  'lightbulb',
  'local_offer',
  'share',
  'bookmark',
  'qr_code_scanner',
  'people',
  'shopping_cart',
  'star',
  'notifications',
  'trending_up',
  'campaign',
  'info',
];

const MarketingMessages: React.FC = () => {
  const [messages, setMessages] = useState<MarketingMessage[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCountryId, setFilterCountryId] = useState<number | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<MarketingMessage | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Form state
  const [formCountryId, setFormCountryId] = useState<number>(0);
  const [formTitle, setFormTitle] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formIconName, setFormIconName] = useState('lightbulb');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formPriority, setFormPriority] = useState(0);
  const [formExpiresAt, setFormExpiresAt] = useState('');

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    loadMessages();
  }, [filterCountryId]);

  const loadCountries = async () => {
    try {
      const data = await fetchCountriesForDropdown();
      setCountries(data);
    } catch (err) {
      console.error('Failed to load countries', err);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await fetchMarketingMessages(filterCountryId);
      setMessages(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const getCountryName = (countryId: number) => {
    const country = countries.find((c) => c.id === countryId);
    return country ? country.name : `Country #${countryId}`;
  };

  const openCreateModal = () => {
    setEditingMessage(null);
    setFormCountryId(filterCountryId || (countries.length > 0 ? countries[0].id : 0));
    setFormTitle('');
    setFormMessage('');
    setFormIconName('lightbulb');
    setFormIsActive(true);
    setFormPriority(0);
    setFormExpiresAt('');
    setModalOpen(true);
  };

  const openEditModal = (msg: MarketingMessage) => {
    setEditingMessage(msg);
    setFormCountryId(msg.country_id);
    setFormTitle(msg.title);
    setFormMessage(msg.message);
    setFormIconName(msg.icon_name || 'lightbulb');
    setFormIsActive(msg.is_active);
    setFormPriority(msg.priority);
    setFormExpiresAt(msg.expires_at ? msg.expires_at.slice(0, 16) : '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingMessage(null);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formMessage.trim() || !formCountryId) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      setSaving(true);
      if (editingMessage) {
        await updateMarketingMessage(editingMessage.id, {
          country_id: formCountryId,
          title: formTitle.trim(),
          message: formMessage.trim(),
          icon_name: formIconName,
          is_active: formIsActive,
          priority: formPriority,
          ...(formExpiresAt
            ? { expires_at: new Date(formExpiresAt).toISOString() }
            : { clear_expires_at: true }),
        });
      } else {
        await createMarketingMessage({
          country_id: formCountryId,
          title: formTitle.trim(),
          message: formMessage.trim(),
          icon_name: formIconName,
          is_active: formIsActive,
          priority: formPriority,
          expires_at: formExpiresAt ? new Date(formExpiresAt).toISOString() : null,
        });
      }
      closeModal();
      await loadMessages();
    } catch (err) {
      alert(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMarketingMessage(id);
      setDeleteConfirmId(null);
      await loadMessages();
    } catch (err) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleToggleActive = async (msg: MarketingMessage) => {
    try {
      await updateMarketingMessage(msg.id, { is_active: !msg.is_active });
      await loadMessages();
    } catch (err) {
      alert(`Failed to update: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="marketing-messages-page">
      <div className="mm-header">
        <div className="mm-header-left">
          <Megaphone size={28} className="mm-header-icon" />
          <div>
            <h1>Marketing Messages</h1>
            <p>Manage promotional messages shown when search returns no results</p>
          </div>
        </div>
        <button className="mm-btn mm-btn-primary" onClick={openCreateModal}>
          <Plus size={18} />
          Add Message
        </button>
      </div>

      {/* Filter bar */}
      <div className="mm-filter-bar">
        <div className="mm-filter-group">
          <Globe size={16} />
          <select
            value={filterCountryId || ''}
            onChange={(e) =>
              setFilterCountryId(e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">All Countries</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <button className="mm-btn mm-btn-secondary" onClick={loadMessages}>
          <RefreshCw size={16} />
          Refresh
        </button>
        <span className="mm-count">{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="mm-loading">Loading...</div>
      ) : error ? (
        <div className="mm-error">{error}</div>
      ) : messages.length === 0 ? (
        <div className="mm-empty">
          <Megaphone size={48} />
          <h3>No marketing messages yet</h3>
          <p>Create your first message to show users when they search with no results.</p>
          <button className="mm-btn mm-btn-primary" onClick={openCreateModal}>
            <Plus size={18} />
            Add Message
          </button>
        </div>
      ) : (
        <div className="mm-table-container">
          <table className="mm-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Title</th>
                <th>Message</th>
                <th>Icon</th>
                <th>Priority</th>
                <th>Views</th>
                <th>Expires</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg) => (
                <tr key={msg.id} className={!msg.is_active ? 'mm-row-inactive' : ''}>
                  <td>
                    <span className="mm-country-badge">{getCountryName(msg.country_id)}</span>
                  </td>
                  <td className="mm-title-cell">{msg.title}</td>
                  <td className="mm-message-cell">{msg.message}</td>
                  <td>
                    <span className="mm-icon-badge">{msg.icon_name || 'lightbulb'}</span>
                  </td>
                  <td className="mm-priority-cell">{msg.priority}</td>
                  <td className="mm-views-cell">
                    <span className="mm-views-badge">{msg.view_count.toLocaleString()}</span>
                  </td>
                  <td className="mm-expires-cell">
                    {msg.expires_at ? (
                      <span className={`mm-expires-badge ${new Date(msg.expires_at) < new Date() ? 'expired' : ''}`}>
                        <Calendar size={12} />
                        {new Date(msg.expires_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="mm-expires-badge never">Never</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={`mm-toggle ${msg.is_active ? 'active' : ''}`}
                      onClick={() => handleToggleActive(msg)}
                      title={msg.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {msg.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                    </button>
                  </td>
                  <td>
                    <div className="mm-actions">
                      <button className="mm-action-btn edit" onClick={() => openEditModal(msg)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                      {deleteConfirmId === msg.id ? (
                        <div className="mm-delete-confirm">
                          <span>Delete?</span>
                          <button className="mm-action-btn danger" onClick={() => handleDelete(msg.id)}>Yes</button>
                          <button className="mm-action-btn" onClick={() => setDeleteConfirmId(null)}>No</button>
                        </div>
                      ) : (
                        <button className="mm-action-btn danger" onClick={() => setDeleteConfirmId(msg.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="mm-modal-overlay" onClick={closeModal}>
          <div className="mm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mm-modal-header">
              <h2>{editingMessage ? 'Edit Message' : 'New Message'}</h2>
              <button className="mm-modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="mm-modal-body">
              <div className="mm-form-group">
                <label>Country *</label>
                <select value={formCountryId} onChange={(e) => setFormCountryId(Number(e.target.value))}>
                  <option value={0} disabled>Select country</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="mm-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Did you know?"
                  maxLength={200}
                />
              </div>
              <div className="mm-form-group">
                <label>Message *</label>
                <textarea
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  placeholder="e.g. Try scanning the barcode to find your product!"
                  rows={3}
                />
              </div>
              <div className="mm-form-row">
                <div className="mm-form-group">
                  <label>Icon</label>
                  <select value={formIconName} onChange={(e) => setFormIconName(e.target.value)}>
                    {ICON_OPTIONS.map((icon) => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                <div className="mm-form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={formPriority}
                    onChange={(e) => setFormPriority(Number(e.target.value))}
                    min={0}
                    max={100}
                  />
                </div>
              </div>
              <div className="mm-form-group">
                <label>Expires At</label>
                <input
                  type="datetime-local"
                  value={formExpiresAt}
                  onChange={(e) => setFormExpiresAt(e.target.value)}
                />
                <small className="mm-form-hint">Leave empty for no expiry</small>
              </div>
              <div className="mm-form-group mm-form-toggle">
                <label>Active</label>
                <button
                  type="button"
                  className={`mm-toggle ${formIsActive ? 'active' : ''}`}
                  onClick={() => setFormIsActive(!formIsActive)}
                >
                  {formIsActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  <span>{formIsActive ? 'Yes' : 'No'}</span>
                </button>
              </div>
            </div>
            <div className="mm-modal-footer">
              <button className="mm-btn mm-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button className="mm-btn mm-btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={16} />
                {saving ? 'Saving...' : editingMessage ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingMessages;
